import type { JSX } from 'react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Input, ModalV1, toast } from '@feathr/components';
import { validate } from '@feathr/rachis';

interface IProps {
  title: string;
  onClose: () => void;
  onConfirm: (emailAddress: string) => Promise<unknown | Error>;
}

function ExportModal({ onClose, onConfirm, title }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [emailAddress, setEmailAddress] = useState('');

  function validateEmailAddress(): string[] {
    const error: string[] = validate.single(emailAddress, {
      presence: {
        allowEmpty: false,
        message: t('^Email address is required.'),
      },
      email: {
        message: t('^Email address must be valid.'),
      },
    });
    return error ?? [];
  }

  function handleChange(newValue?: string): void {
    setEmailAddress(newValue ?? '');
  }

  async function handleConfirm(): Promise<void> {
    const result = await onConfirm(emailAddress);
    if (result instanceof Error) {
      toast(t("Couldn't prepare report. Try again."), {
        type: 'error',
      });
    } else {
      toast(t('Your export is on the way! Check your email inbox in a few minutes.'), {
        type: 'success',
      });
    }
    onClose();
  }

  return (
    <ModalV1
      confirmButtonText={t('Export')}
      confirmDisabled={validateEmailAddress().length > 0}
      controlled={true}
      ephemeral={true}
      onClose={onClose}
      onConfirm={handleConfirm}
      t={t}
      title={title}
    >
      <div>
        <Trans t={t}>
          <p>
            Input an email address below and click export.
            <br />
            Afterward you should receive an email at the provided address with the requested data in{' '}
            <code>.csv</code> format.
          </p>
        </Trans>
        <Input
          onChange={handleChange}
          placeholder={t('Email address')}
          type={'text'}
          validationError={validateEmailAddress()}
          value={emailAddress}
        />
      </div>
    </ModalV1>
  );
}

export default ExportModal;
